const titlesSelector =
  '.landing-page-img-fullsize .content .landing-page-img-fullsize__main-title';

const fullsizeSectionContentSelector = '.landing-page-img-fullsize';
const fullsizeSectionMarkersSelector = `.template-index ${fullsizeSectionContentSelector}`;

const textSelector = '[data-test-ab-handle]:not([data-test-text]), [data-test-text="true"]';

let frameId = null;
const titles = new Set();
const pendingTitles = new Set();

export function positionFullsizeTitleTooltips(
  titlesToPosition = [...titles]
) {
  titlesToPosition.forEach((title) => {
    if (!title.isConnected) {
      return;
    }

    const text = [...title.querySelectorAll(textSelector)].find((element) => {
      const style = window.getComputedStyle(element);

      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    const tooltip = [...title.children].find((element) =>
      element.hasAttribute('data-gld-tooltip')
    );

    if (!text || !tooltip) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(text);

    const rects = [...range.getClientRects()].filter((rect) => {
      return rect.width > 0 && rect.height > 0;
    });

    range.detach();

    if (!rects.length) {
      return;
    }

    const lastRect = rects[rects.length - 1];
    const titleRect = title.getBoundingClientRect();

    title.style.setProperty(
      '--gld-title-tooltip-left',
      `${lastRect.right - titleRect.left + 6}px`
    );
    title.style.setProperty(
      '--gld-title-tooltip-top',
      `${lastRect.top - titleRect.top + lastRect.height / 2}px`
    );
  });
}

function queuePositionUpdate(titlesToPosition = [...titles]) {
  titlesToPosition.forEach((title) => pendingTitles.add(title));

  if (!pendingTitles.size || frameId !== null) {
    return;
  }

  frameId = window.requestAnimationFrame(() => {
    frameId = null;

    const queuedTitles = [...pendingTitles];
    pendingTitles.clear();

    positionFullsizeTitleTooltips(queuedTitles);
  });
}

function registerTitles(root) {
  const matchedTitles = [];

  if (root.matches?.(titlesSelector)) {
    matchedTitles.push(root);
  }

  root.querySelectorAll?.(titlesSelector).forEach((title) => {
    matchedTitles.push(title);
  });

  const newTitles = matchedTitles.filter((title) => !titles.has(title));

  newTitles.forEach((title) => {
    titles.add(title);

    if (window.MutationObserver) {
      const observer = new MutationObserver(() => {
        queuePositionUpdate([title]);
      });

      observer.observe(title, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  });

  queuePositionUpdate(newTitles);
}

function observeFullsizeSection(section) {
  const hasLoadedContent = () =>
    section.matches?.(fullsizeSectionContentSelector) ||
    section.querySelector?.(fullsizeSectionContentSelector);

  if (hasLoadedContent()) {
    registerTitles(section);
    return;
  }

  if (!window.MutationObserver) {
    return;
  }

  const observer = new MutationObserver(() => {
    if (!hasLoadedContent()) {
      return;
    }

    observer.disconnect();
    registerTitles(section);
  });

  observer.observe(section, {
    childList: true,
    subtree: true,
  });
}

export default function initFullsizeTitleTooltipPosition() {
  if (window.gldFullsizeTitleTooltipsInitialized) {
    return;
  }

  const sections = new Set();

  document.querySelectorAll(fullsizeSectionMarkersSelector).forEach((marker) => {
    sections.add(marker.closest('.shopify-section') || marker);
  });

  if (!sections.size) {
    return;
  }

  window.gldFullsizeTitleTooltipsInitialized = true;
  window.gldPositionFullsizeTitleTooltips = () =>
    queuePositionUpdate([...titles]);

  sections.forEach(observeFullsizeSection);

  if (document.readyState !== 'complete') {
    window.addEventListener(
      'load',
      () => queuePositionUpdate([...titles]),
      { once: true }
    );
  }

  window.addEventListener('resize', () => queuePositionUpdate([...titles]), {
    passive: true,
  });

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => queuePositionUpdate([...titles]));
  }

  queuePositionUpdate([...titles]);
}
